import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, RefreshCcw, Circle, Copy, Webhook, CheckCircle2, AlertTriangle } from "lucide-react";

const STATUS_STYLES = {
  sent:       { color: "#C4A265", label: "Sent" },
  delivered:  { color: "#4E7A5C", label: "Delivered" },
  opened:     { color: "#4E7A5C", label: "Opened" },
  clicked:    { color: "#4E7A5C", label: "Clicked" },
  bounced:    { color: "#B54D4D", label: "Bounced" },
  failed:     { color: "#B54D4D", label: "Failed" },
  complained: { color: "#B54D4D", label: "Complained" },
  mocked:     { color: "rgba(10,37,64,0.5)", label: "Mocked" },
  queued:     { color: "rgba(10,37,64,0.6)", label: "Queued" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { color: "rgba(10,37,64,0.5)", label: status || "unknown" };
  return (
    <span data-testid={`email-status-${status}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-medium"
      style={{ backgroundColor: `${s.color}22`, color: s.color }}>
      <Circle className="w-2 h-2 fill-current" strokeWidth={0} />
      {s.label}
    </span>
  );
}

export default function AdminEmails() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [webhookCfg, setWebhookCfg] = useState(null);
  const [showWebhookGuide, setShowWebhookGuide] = useState(false);

  const load = async () => {
    const [s, r, w] = await Promise.all([
      api.get("/admin/emails/stats").then((x) => x.data),
      api.get("/admin/emails").then((x) => x.data),
      api.get("/admin/emails/webhook-config").then((x) => x.data).catch(() => null),
    ]);
    setStats(s); setRows(r); setWebhookCfg(w);
  };
  useEffect(() => { load(); }, []);

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testTo) return;
    setSendingTest(true);
    try {
      await api.post("/admin/emails/test", { to: testTo });
      toast.success(`Test email queued to ${testTo}`);
      setTestTo("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    setSendingTest(false);
  };

  const refresh = async (id) => {
    setRefreshingId(id);
    try {
      await api.post(`/admin/emails/${id}/refresh`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Refresh failed"); }
    setRefreshingId(null);
  };

  if (!stats) return <div className="text-navy/60">Loading…</div>;

  const cards = [
    { label: "Total",     value: stats.total,     tid: "emails-total" },
    { label: "Sent",      value: stats.sent,      tid: "emails-sent",      color: "#C4A265" },
    { label: "Delivered", value: stats.delivered, tid: "emails-delivered", color: "#4E7A5C" },
    { label: "Bounced",   value: stats.bounced,   tid: "emails-bounced",   color: "#B54D4D" },
    { label: "Failed",    value: stats.failed,    tid: "emails-failed",    color: "#B54D4D" },
  ];

  return (
    <div data-testid="admin-emails-page">
      <div className="eyebrow mb-2">Deliverability</div>
      <h1 className="aperture-h1 text-4xl mb-2">Emails.</h1>
      <p className="text-sm text-navy/60 mb-8">
        Live log of every transactional email sent by Aperture — receipts, password resets, welcomes, broadcasts, affiliate confirmations.
      </p>

      <div className="mb-6 bg-white p-5 rounded-sm border flex flex-col md:flex-row md:items-center md:justify-between gap-3" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
        <div>
          <div className="eyebrow" data-testid="emails-configured">
            {stats.configured ? <span style={{ color: "#4E7A5C" }}>● Configured</span> : <span style={{ color: "#B54D4D" }}>● Not configured</span>}
          </div>
          <div className="mt-1 text-sm text-navy">Sender · <span className="text-navy/70 font-mono">{stats.sender}</span></div>
        </div>
        <form onSubmit={sendTest} className="flex gap-2 items-center" data-testid="email-test-form">
          <input type="email" required value={testTo} onChange={(e)=>setTestTo(e.target.value)} placeholder="you@example.com" data-testid="email-test-to"
            className="px-3 py-2 border rounded-sm text-sm focus:outline-none focus:border-gold w-64" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
          <button disabled={sendingTest} data-testid="email-test-send" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 rounded-sm">
            <Send className="w-3.5 h-3.5"/>{sendingTest ? "Sending…" : "Send Test"}
          </button>
        </form>
      </div>

      {webhookCfg && (
        <div className="mb-8 bg-white p-5 rounded-sm border" style={{ borderColor: "rgba(10,37,64,0.10)" }} data-testid="webhook-config-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Webhook className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <div className="eyebrow">Real-time Delivery Tracking</div>
                {webhookCfg.secret_configured
                  ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ backgroundColor: "rgba(78,122,92,0.15)", color: "#4E7A5C" }}><CheckCircle2 className="w-3 h-3"/>Active</span>
                  : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ backgroundColor: "rgba(181,77,77,0.15)", color: "#B54D4D" }}><AlertTriangle className="w-3 h-3"/>Not Configured</span>
                }
              </div>
              <div className="text-sm text-navy/70 mb-3">
                {webhookCfg.secret_configured
                  ? "Resend is pushing delivery events to us in real time. Statuses update automatically as emails are delivered, opened, bounced, or complained about."
                  : "Configure a webhook in Resend to receive real-time delivery status. Bounced/complained addresses will be auto-flagged."}
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-navy/5 px-3 py-2 rounded-sm font-mono text-navy/80" data-testid="webhook-url">{webhookCfg.webhook_url}</code>
                <button onClick={()=>{navigator.clipboard.writeText(webhookCfg.webhook_url); toast.success("URL copied");}} data-testid="webhook-copy"
                  className="px-3 py-2 border border-navy text-navy text-xs hover:bg-navy hover:text-white transition-colors rounded-sm inline-flex items-center gap-1">
                  <Copy className="w-3 h-3"/>Copy
                </button>
              </div>
              <button onClick={()=>setShowWebhookGuide(!showWebhookGuide)} data-testid="webhook-guide-toggle" className="mt-3 text-xs text-gold hover:underline">
                {showWebhookGuide ? "Hide" : "Show"} setup instructions
              </button>
              {showWebhookGuide && (
                <ol className="mt-4 space-y-2 text-sm text-navy/75 list-decimal list-inside bg-navy/5 p-4 rounded-sm" data-testid="webhook-guide">
                  <li>Go to <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer" className="text-gold hover:underline">resend.com/webhooks</a> → <b>Add Webhook</b>.</li>
                  <li>Paste the URL above into the <b>Endpoint URL</b> field.</li>
                  <li>Enable these events: <span className="font-mono text-xs">{webhookCfg.events_to_enable.join(", ")}</span></li>
                  <li>Save the webhook. Resend will show you a <b>Signing Secret</b> starting with <code>whsec_…</code></li>
                  <li>Copy that secret and paste it in this chat. I'll wire it into <code>RESEND_WEBHOOK_SECRET</code>.</li>
                  <li>Redeploy — real-time tracking will go live on production.</li>
                </ol>
              )}
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.tid} className="bg-white p-5 rounded-sm border" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
            <div className="eyebrow" style={c.color ? { color: c.color } : {}}>{c.label}</div>
            <div className="font-serif text-3xl text-navy mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-sm border overflow-x-auto" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
        <table className="w-full text-sm">
          <thead className="bg-navy/5"><tr>{["Sent","To","Subject","Status","Resend ID","Actions"].map(h=><th key={h} className="px-4 py-3 text-left eyebrow" style={{color:"#0A2540"}}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-navy/50">No emails sent yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t align-top" data-testid={`email-row-${r.id}`} style={{ borderColor: "rgba(10,37,64,0.08)" }}>
                <td className="px-4 py-3 text-navy/60 text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">{r.to}</td>
                <td className="px-4 py-3 text-navy/70 max-w-[280px] truncate" title={r.subject}>{r.subject}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={r.status} />
                  {r.error && <div className="mt-1 text-[10px] text-red-600 max-w-[200px] leading-tight">{r.error}</div>}
                </td>
                <td className="px-4 py-3 text-navy/50 text-[10px] font-mono">{r.resend_id ? r.resend_id.slice(0, 8) + "…" : "—"}</td>
                <td className="px-4 py-3">
                  {r.resend_id && (
                    <button onClick={()=>refresh(r.id)} disabled={refreshingId === r.id} data-testid={`email-refresh-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold">
                      <RefreshCcw className={`w-3 h-3 ${refreshingId === r.id ? "animate-spin" : ""}`} />
                      {refreshingId === r.id ? "…" : "Refresh"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-navy/50 leading-relaxed">
        Note: Resend delivery statuses ("delivered", "opened", "bounced") arrive asynchronously.
        Click <b>Refresh</b> on any row to pull the latest status directly from Resend.
      </div>
    </div>
  );
}
